import Vue, {defineAsyncComponent} from 'vue'
import VueRouter from 'vue-router'
import { createRouter } from 'vue-router';
import { createWebHistory } from 'vue-router';
import { canNavigate } from '@/libs/acl/routeProtection'
import Login from '@/views/Login.vue'
import Faq from '@/views/Faq.vue'
import Recovery from '@/views/Recovery.vue'
import Book from '@/views/BookPackage.vue'
import Companies from '@/views/Companies.vue'
import Units from '@/views/Units.vue'
import UnitCategories from '@/views/UnitCategories.vue'
import Services from '@/views/Services.vue'
import ServiceCategories from '@/views/ServiceCategories.vue'
import StockLocations from '@/views/StockLocations.vue'
import Suppliers from '@/views/Suppliers.vue'
import Dashboard from '@/views/Dashboard.vue'
import Calendar from '@/views/Calendar.vue'
import Home from '@/views/Home.vue'

import store from '@/store'
import { canStaffCreate, isStaffMember, staffMustChangePassword } from '@/libs/staffPermissions'
import ability from '@/libs/acl/ability'
import { isPublicAuthPath } from '@/libs/sessionWatch'

const routes = [
  {
    path: '/',
    name: 'home',
    component: Home,
    meta: {
      layout: 'full',
      action: 'read',
      resource: 'Page',
    },
  },
  {
    path: '/system-status',
    name: 'system-status',
    component: () => import('@/views/SystemStatus.vue'),
    meta: {
      layout: 'full',
      action: 'read',
      resource: 'Page',
    },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: Dashboard,
    meta: {

      action: 'read',
      resource: 'Customer',
    },
  },
  {
    path: '/charter-survey/:saleId',
    name: 'charter-survey',
    component: () => import('@/views/CharterSurvey.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      isAuthenticated: true,
    },
  },
  {
    path: '/book',
    name: 'book-package',
    component: Book,
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      isAuthenticated: true,
    },
  },
  {
    path: '/check-my-charter',
    name: 'check-my-charter',
    component: () => import('@/views/CheckMyCharter.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      isAuthenticated: true,
    },
  },
  {
    path: '/package-preparations',
    name: 'package-preparations',
    component: () => import('@/views/PackagePreparations.vue'),
    meta: {
      action: 'read',
      resource: 'Page',
    },
  },
  {
    path: '/faq',
    name: 'faq',
    component: Faq,
    meta: {
      action: 'read',
      resource: 'Page',
    },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/Admin.vue'),
    meta: {
      action: 'read',
      resource: 'Page',
      isAuthenticated: true,
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/Profile.vue'),
    meta: {
      action: 'read',
      resource: 'Page',
      isAuthenticated: true,
    },
  },
  {
    path: '/password',
    name: 'password',
    component: () => import('@/views/ChangePassword.vue'),
    meta: {
      action: 'read',
      resource: 'Page',
      isAuthenticated: true,
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      action: 'read',
      resource: 'Page',
      isAuthenticated: true,
    },
    beforeEnter(to, from, next) {
      store.dispatch('auth/logout').then(() => {
        next({ name: 'login' })
      }).catch(() => {
        next({ name: 'login' })
      })
    },
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      layout: 'full',
      action: 'read',
      resource: 'Page',
      redirectIfLoggedIn: true,
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/Register.vue'),
    meta: {
      layout: 'full',
      action: 'read',
      resource: 'Page',
      redirectIfLoggedIn: true,
    },
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('@/views/Contact.vue'),
    meta: {
      layout: 'full',
      action: 'read',
      resource: 'Page',
    },
  },
  {
    path: '/documentation/:docPath(.*)*',
    name: 'documentation',
    component: () => import('@/views/Documentation.vue'),
    meta: {
      layout: 'full',
      action: 'read',
      resource: 'Page',
    },
  },
  {
    path: '/forgot-password',
    name: 'recovery',
    component: Recovery,
    meta: {
      layout: 'full',
      action: 'read',
      resource: 'Page',
      redirectIfLoggedIn: true,
    },
  },
  {
    path: '/reset-password/:token',
    name: 'reset',
    component: () => import('@/views/Reset.vue'),
    meta: {
      layout: 'full',
      action: 'read',
      resource: 'Page',
      redirectIfLoggedIn: true,
    },
  },
  {
    path: '/admin/bookings',
    name: 'bookings',
    component: () => import('@/views/Sales.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/orders',
    name: 'orders',
    component: () => import('@/views/Sales.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/returned-orders',
    name: 'returned_orders',
    component: () => import('@/views/Sales.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      withReturns: true,
    },
  },
  {
    path: '/admin/bookings/customer/:customerId',
    name: 'bookings_by_user',
    component: () => import('@/views/Sales.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      navOpenGroup: 'Bookings',
    },
  },
  {
    path: '/admin/orders/customer/:customerId',
    name: 'orders_by_user',
    component: () => import('@/views/Sales.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      navOpenGroup: 'Orders',
    },
  },
  {
    path: '/admin/repeat-bookings',
    name: 'bookings_repeat',
    component: () => import('@/views/Sales.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/repeat-orders',
    name: 'orders_repeat',
    component: () => import('@/views/Sales.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/packages',
    name: 'packages',
    component: () => import('@/views/Packages.vue'),
    meta: {
      action: 'manage',
      resource: 'Admin',
    },
  },
  {
    path: '/admin/units',
    name: 'units',
    component: Units,
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/products',
    name: 'products',
    component: Units,
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/stockLocations',
    name: 'stockLocations',
    component: StockLocations,
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/suppliers',
    name: 'suppliers',
    component: Suppliers,
    meta: {
      action: 'manage',
      resource: 'Company Owner',
    },
  },
  {
    path: '/admin/unitCategories',
    name: 'unitCategories',
    component: UnitCategories,
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/productCategories',
    name: 'productCategories',
    component: UnitCategories,
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/services',
    name: 'services',
    component: Services,
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/serviceCategories',
    name: 'serviceCategories',
    component: ServiceCategories,
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/company/settings',
    name: 'company-settings',
    component: () => import('@/views/CompanySettings.vue'),
    meta: {
      action: 'manage',
      resource: 'Company Owner',
    },
  },
  {
    path: '/plan-and-billing',
    name: 'plan-and-billing',
    component: () => import('@/views/PlanAndBilling.vue'),
    meta: {
      action: 'manage',
      resource: 'Company Owner',
    },
  },
  {
    path: '/admin/company/staff',
    name: 'staff',
    component: () => import('@/views/Staff.vue'),
    meta: {
      action: 'manage',
      resource: 'Company Owner',
    },
  },
  {
    path: '/admin/companies',
    name: 'companies',
    component: Companies,
    meta: {
      action: 'manage',
      resource: 'Admin',
    },
  },
  {
    path: '/admin/announcements',
    name: 'announcements',
    component: () => import('@/views/Announcements.vue'),
    meta: {
      action: 'manage',
      resource: 'Admin',
    },
  },
  {
    path: '/admin/customers',
    name: 'customers',
    component: () => import('@/views/Customers.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/customers/repeat-order-customers',
    name: 'repeat-customers',
    component: () => import('@/views/Customers.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/users',
    name: 'users',
    component: () => import('@/views/Users.vue'),
    meta: {
      action: 'manage',
      resource: 'Admin',
    },
  },
  {
    path: '/admin/daily-log',
    name: 'daily-log',
    component: () => import('@/views/DailyLog.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/calendar',
    name: 'calendar',
    component: Calendar,
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/dashboard/sales',
    name: 'dashboard-sales',
    component: () => import('@/views/DashboardSales.vue'),
    meta: {
      action: 'manage',
      resource: 'Company Owner',
    },
  },
  {
    path: '/admin/dashboard/returns',
    name: 'dashboard-returns',
    component: () => import('@/views/DashboardReturns.vue'),
    meta: {
      action: 'manage',
      resource: 'Company Owner',
    },
  },
  {
    path: '/admin/dashboard/customers',
    name: 'dashboard-customers',
    component: () => import('@/views/DashboardCustomers.vue'),
    meta: {
      action: 'manage',
      resource: 'Company Owner',
    },
  },
  {
    path: '/admin/dashboard/staff',
    name: 'dashboard-staff',
    component: () => import('@/views/DashboardStaff.vue'),
    meta: {
      action: 'manage',
      resource: 'Company Owner',
    },
  },
  {
    path: '/admin/new-customer',
    name: 'new-customer',
    component: () => import('@/views/CreateCustomer.vue'),
    meta: {
      action: 'manage',
      resource: 'Admin',
    },
  },
  {
    path: '/admin/new-booking',
    name: 'new-booking',
    component: () => import('@/views/CreateSale.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/new-order',
    name: 'new-order',
    component: () => import('@/views/CreateSale.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
    },
  },
  {
    path: '/admin/crtcreator/channels',
    name: 'crtcreator-channels',
    component: () => import('@/views/Modules/crtcreator/Channels.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      navOpenGroup: 'Content Creator',
      modulePrefix: 'crtcreator',
    },
  },
  {
    path: '/admin/crtcreator/content-categories',
    name: 'crtcreator-content-categories',
    component: () => import('@/views/Modules/crtcreator/ContentCategories.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      navOpenGroup: 'Content Creator',
      modulePrefix: 'crtcreator',
    },
  },
  {
    path: '/admin/crtcreator/content',
    name: 'crtcreator-content',
    component: () => import('@/views/Modules/crtcreator/Content.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      navOpenGroup: 'Content Creator',
      modulePrefix: 'crtcreator',
    },
  },
  {
    path: '/admin/crtcreator/posts',
    name: 'crtcreator-posts',
    component: () => import('@/views/Modules/crtcreator/Posts.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      navOpenGroup: 'Content Creator',
      modulePrefix: 'crtcreator',
    },
  },
  {
    path: '/admin/crtcreator/post-types',
    name: 'crtcreator-post-types',
    component: () => import('@/views/Modules/crtcreator/PostTypes.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      navOpenGroup: 'Content Creator',
      modulePrefix: 'crtcreator',
    },
  },
  {
    path: '/admin/crtcreator/resources',
    name: 'crtcreator-resources',
    component: () => import('@/views/Modules/crtcreator/Resources.vue'),
    meta: {
      action: 'manage',
      resource: 'Supervisor',
      navOpenGroup: 'Content Creator',
      modulePrefix: 'crtcreator',
    },
  },
  {
    path: '/error-404',
    name: 'error-404',
    component: () => import('@/views/error/Error404.vue'),
    meta: {
      layout: 'full',
      action: 'read',
      resource: 'Page',
    },
  },
  {
    path: "/:catchAll(.*)",
    redirect: 'error-404',
  },
];

/*
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  mode: 'history',
  base: process.env.BASE_URL
})*/

const router = createRouter({
  history: createWebHistory(), // no BASE_URL here
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})


router.beforeEach(async (to, from) => {
  // Public pages paint immediately; protected routes wait for the boot auth probe.
  if (!isPublicAuthPath(to.path)) {
    await store.dispatch('auth/ensureBootstrapped', ability)
  } else if (!store.getters['auth/bootstrapped']) {
    store.dispatch('auth/bootstrap', ability)
  }

  const isLoggedIn = store.getters['auth/authenticated']
  const user = store.getters['auth/user']

  if (!isLoggedIn && (to.meta.isAuthenticated || to.path.includes('admin'))) {
    return { name: 'login' }
  }

  if (isLoggedIn && staffMustChangePassword(user) && to.name !== 'password' && to.name !== 'logout') {
    return { name: 'password' }
  }

  if (
    isLoggedIn
    && (to.name === 'new-order' || to.name === 'new-booking')
    && isStaffMember(user)
    && !canStaffCreate(user, 'orders')
  ) {
    return { name: 'error-404' }
  }

  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    return { name: 'profile' }
  }

  if (
    to.name === 'home'
    && user
    && (user.role === 'Supervisor' || user.role === 'Admin')
  ) {
    return { name: 'calendar' }
  }

  if (isLoggedIn && to.meta.modulePrefix) {
    const staffRoles = ['Admin', 'Company Owner', 'Supervisor']
    if (staffRoles.includes(user?.role) && !store.getters['modulesRegistry/loaded']) {
      try {
        await store.dispatch('modulesRegistry/loadModules')
      } catch (e) {
        return { name: 'error-404' }
      }
    }

    if (!store.getters['modulesRegistry/hasModule'](to.meta.modulePrefix)) {
      return { name: 'error-404' }
    }
  }

  if (!canNavigate(to)) {
    if (!isLoggedIn) {
      return { name: 'login' }
    }

    return { name: 'error-404' }
  }
})

export default router
